// src/pages/Seller/Invoices.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Avatar, Tooltip, LinearProgress,
  Tabs, Tab, Alert, Divider,
} from '@mui/material';
import {
  Search, Refresh, Visibility, Receipt, AttachMoney,
  Schedule, CheckCircle, Warning, Download, Send,
} from '@mui/icons-material';
import { sellerAPI } from '../../services/api';
import toast from 'react-hot-toast';

const fmt = (v) =>
  new Intl.NumberFormat('en-US', { style:'currency', currency:'USD' }).format(v || 0);

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }); }
  catch { return '—'; }
};

const STATUS_COLOR = {
  pending:'warning', sent:'info', paid:'success',
  overdue:'error', cancelled:'default',
};

const getId  = (inv) => inv?.id || inv?._id || '';
const getNum = (inv) => inv?.invoice_number || inv?.invoiceNumber || `INV-${getId(inv)}`;
const getAmt = (inv) => inv?.total_amount   || inv?.totalAmount   || 0;

const TAB_STATUSES = ['all','pending','sent','paid','overdue'];

const SellerInvoices = () => {
  const [invoices,   setInvoices]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue,   setTabValue]   = useState(0);
  const [selected,   setSelected]   = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [sending,    setSending]    = useState(null);

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res  = await sellerAPI.getMyInvoices();
      const data = res.data;
      setInvoices(Array.isArray(data) ? data : (data.invoices || []));
    } catch (err) {
      // Graceful — seller may not have Invoice model yet
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (inv) => {
    const id = getId(inv);
    setSending(id);
    try {
      await sellerAPI.sendInvoice(id);
      toast.success('Invoice sent to buyer!');
      fetchInvoices();
    } catch {
      toast.error('Failed to send invoice');
    } finally {
      setSending(null);
    }
  };

  const handleDownload = async (inv) => {
    const num = getNum(inv);
    try {
      toast.success(`Downloading invoice ${num}...`);
      // Generate simple text invoice for now
      const content = [
        'INVOICE', '='.repeat(40),
        `Invoice: ${num}`,
        `Enquiry: #${inv.enquiry_id || inv.enquiryId || '—'}`,
        `Date: ${fmtDate(inv.created_at || inv.createdAt)}`,
        `Amount: ${fmt(getAmt(inv))}`,
        `Status: ${inv.status || 'pending'}`,
        '',
        'Thank you for your business.',
      ].join('\n');
      const blob = new Blob([content], { type:'text/plain' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${num}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const stats = {
    total:         invoices.length,
    pending:       invoices.filter(i => i.status === 'pending').length,
    paid:          invoices.filter(i => i.status === 'paid').length,
    overdue:       invoices.filter(i => i.status === 'overdue').length,
    totalRevenue:  invoices.filter(i => i.status === 'paid').reduce((s, i) => s + getAmt(i), 0),
    pendingAmount: invoices.filter(i => ['pending','sent','overdue'].includes(i.status))
                           .reduce((s, i) => s + getAmt(i), 0),
  };

  const statusFilter = TAB_STATUSES[tabValue];
  const filtered = invoices.filter(inv => {
    const q = searchTerm.toLowerCase();
    const ok = !q ||
      getNum(inv).toLowerCase().includes(q) ||
      String(inv.enquiry_id || inv.enquiryId || '').includes(q) ||
      (inv.buyer_name || inv.buyerName || '').toLowerCase().includes(q);
    return ok && (statusFilter === 'all' || inv.status === statusFilter);
  });

  if (loading) return (
    <Container sx={{ py:4 }}>
      <LinearProgress sx={{ mb:2 }} />
      <Typography color="text.secondary">Loading invoices...</Typography>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ py:3 }}>

      {/* Header */}
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Invoices</Typography>
          <Typography color="text.secondary">Manage and send invoices to buyers</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchInvoices}>Refresh</Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb:4 }}>
        {[
          { label:'Total Invoices', value: stats.total,                     color:'#2196f3', icon:<Receipt /> },
          { label:'Pending',        value: stats.pending,                    color:'#ff9800', icon:<Schedule />, textColor:'warning.main' },
          { label:'Pending Amount', value: fmt(stats.pendingAmount),         color:'#f44336', icon:<AttachMoney />, isText:true, textColor:'error.main' },
          { label:'Total Revenue',  value: fmt(stats.totalRevenue),          color:'#4caf50', icon:<CheckCircle />, isText:true, textColor:'success.main' },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ borderRadius:2 }}>
              <CardContent>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">{s.label}</Typography>
                    <Typography fontWeight="bold" color={s.textColor || 'text.primary'}
                      sx={s.isText ? { fontSize:'1.3rem' } : { fontSize:'2rem' }}>
                      {s.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor:`${s.color}20`, color:s.color }}>{s.icon}</Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb:3, borderRadius:2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable">
          <Tab label={`All (${stats.total})`}    icon={<Receipt />}     iconPosition="start" />
          <Tab label={`Pending (${stats.pending})`} icon={<Schedule />}  iconPosition="start" />
          <Tab label="Sent"                       icon={<Send />}        iconPosition="start" />
          <Tab label={`Paid (${stats.paid})`}    icon={<CheckCircle />} iconPosition="start" />
          <Tab label={`Overdue (${stats.overdue})`} icon={<Warning />}  iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Search */}
      <Paper sx={{ p:2, mb:3, borderRadius:2 }}>
        <TextField fullWidth size="small"
          placeholder="Search by invoice number, enquiry ID, or buyer..."
          value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          InputProps={{ startAdornment:<InputAdornment position="start"><Search /></InputAdornment> }}
        />
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius:2, overflow:'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor:'grey.50' }}>
              <TableRow>
                {['Invoice #','Enquiry','Buyer','Amount','Status','Date','Actions'].map(h => (
                  <TableCell key={h}><Typography variant="subtitle2" fontWeight="bold">{h}</Typography></TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py:8 }}>
                    <Receipt sx={{ fontSize:64, color:'text.disabled', display:'block', mx:'auto', mb:2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {invoices.length === 0 ? 'No invoices yet' : 'No invoices match filter'}
                    </Typography>
                    {invoices.length === 0 && (
                      <Alert severity="info" sx={{ maxWidth:400, mx:'auto', mt:2 }}>
                        Invoices are created by the admin when an enquiry is confirmed.
                        You can view and send them to buyers from here.
                      </Alert>
                    )}
                  </TableCell>
                </TableRow>
              ) : filtered.map(inv => {
                const id  = getId(inv);
                const num = getNum(inv);
                const amt = getAmt(inv);
                return (
                  <TableRow key={id} hover>
                    <TableCell><Typography variant="body2" fontWeight="medium">{num}</Typography></TableCell>
                    <TableCell><Typography variant="body2">#{inv.enquiry_id || inv.enquiryId || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{inv.buyer_name || inv.buyerName || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight="medium">{fmt(amt)}</Typography></TableCell>
                    <TableCell>
                      <Chip label={inv.status || 'pending'} size="small"
                        color={STATUS_COLOR[inv.status] || 'default'}
                        sx={{ textTransform:'capitalize' }} />
                    </TableCell>
                    <TableCell>{fmtDate(inv.created_at || inv.createdAt)}</TableCell>
                    <TableCell>
                      <Box sx={{ display:'flex', gap:0.5 }}>
                        <Tooltip title="View">
                          <IconButton size="small" color="primary"
                            onClick={() => { setSelected(inv); setViewDialog(true); }}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton size="small" color="secondary" onClick={() => handleDownload(inv)}>
                            <Download />
                          </IconButton>
                        </Tooltip>
                        {['pending','sent'].includes(inv.status) && (
                          <Tooltip title="Send to Buyer">
                            <IconButton size="small" color="success"
                              disabled={sending === id}
                              onClick={() => handleSend(inv)}>
                              <Send />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p:2, borderTop:1, borderColor:'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filtered.length} of {invoices.length} invoices
          </Typography>
        </Box>
      </Paper>

      {/* View Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
        {selected && (() => {
          const id  = getId(selected);
          const num = getNum(selected);
          const amt = getAmt(selected);
          return (
            <>
              <DialogTitle>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <Typography variant="h6">{num}</Typography>
                  <Chip label={selected.status || 'pending'}
                    color={STATUS_COLOR[selected.status] || 'default'}
                    sx={{ textTransform:'capitalize' }} />
                </Box>
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt:0.5 }}>
                  {[
                    ['Invoice #',    num],
                    ['Enquiry ID',   `#${selected.enquiry_id || selected.enquiryId || '—'}`],
                    ['Buyer',        selected.buyer_name || selected.buyerName || '—'],
                    ['Buyer Email',  selected.buyer_email || selected.buyerEmail || '—'],
                    ['Issue Date',   fmtDate(selected.created_at || selected.createdAt)],
                    ['Due Date',     fmtDate(selected.due_date || selected.dueDate)],
                    ['Payment Terms',selected.payment_terms || 'Net 7 days'],
                  ].filter(([, v]) => v && v !== '—').map(([label, value]) => (
                    <Grid item xs={6} key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight="medium">{value}</Typography>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Divider sx={{ my:1 }} />
                    <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                      <Typography variant="h6">Total Amount:</Typography>
                      <Typography variant="h6" color="primary" fontWeight="bold">{fmt(amt)}</Typography>
                    </Box>
                  </Grid>
                  {selected.notes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Notes</Typography>
                      <Typography variant="body2">{selected.notes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setViewDialog(false)}>Close</Button>
                <Button variant="outlined" startIcon={<Download />} onClick={() => handleDownload(selected)}>
                  Download
                </Button>
                {['pending','sent'].includes(selected.status) && (
                  <Button variant="contained" startIcon={<Send />}
                    onClick={() => { setViewDialog(false); handleSend(selected); }}>
                    Send to Buyer
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Container>
  );
};

export default SellerInvoices;